import { forge } from '@/lib/ezhard';
import { where } from '@ezhard/sdk';
import type { Repository } from '@/data/repository';
import type {
  Organization,
  Position,
  Member,
  Shift,
  ShiftStatus,
  AssignmentSchedule,
  BurdenScore,
  WorkspaceExport,
} from '@/shared/types/entities';

export class ForgeRepository implements Repository {
  /* ─── Organization ─────────────────────────────────────── */

  async getOrganization(): Promise<Organization | undefined> {
    const snap = await forge.doc('organization/org').get();
    return snap.exists ? (snap.data() as unknown as Organization) : undefined;
  }

  async saveOrganization(org: Organization): Promise<void> {
    await forge.doc('organization/org').set({ ...org });
  }

  /* ─── Positions ────────────────────────────────────────── */

  async getPositions(): Promise<Position[]> {
    const snap = await forge.collection('positions').query().get();
    return snap.docs.map((doc) => doc.data() as unknown as Position);
  }

  async getPosition(id: string): Promise<Position | undefined> {
    const snap = await forge.doc(`positions/${id}`).get();
    return snap.exists ? (snap.data() as unknown as Position) : undefined;
  }

  async savePosition(position: Position): Promise<void> {
    await forge.doc(`positions/${position.id}`).set({ ...position });
  }

  async deletePosition(id: string): Promise<void> {
    await forge.doc(`positions/${id}`).delete();
  }

  /* ─── Members ──────────────────────────────────────────── */

  async getMembers(): Promise<Member[]> {
    const snap = await forge.collection('members').query().get();
    return snap.docs.map((doc) => doc.data() as unknown as Member);
  }

  async getActiveMembers(): Promise<Member[]> {
    const snap = await forge
      .collection('members')
      .query(where('isActive', '==', true))
      .get();
    return snap.docs.map((doc) => doc.data() as unknown as Member);
  }

  async getMember(id: string): Promise<Member | undefined> {
    const snap = await forge.doc(`members/${id}`).get();
    return snap.exists ? (snap.data() as unknown as Member) : undefined;
  }

  async saveMember(member: Member): Promise<void> {
    await forge.doc(`members/${member.id}`).set({ ...member });
  }

  async deleteMember(id: string): Promise<void> {
    await forge.doc(`members/${id}`).delete();
  }

  /* ─── Shifts ───────────────────────────────────────────── */

  async getShifts(): Promise<Shift[]> {
    const snap = await forge.collection('shifts').query().get();
    return snap.docs.map((doc) => doc.data() as unknown as Shift);
  }

  async getShiftsByStatus(status: ShiftStatus): Promise<Shift[]> {
    const snap = await forge
      .collection('shifts')
      .query(where('status', '==', status))
      .get();
    return snap.docs.map((doc) => doc.data() as unknown as Shift);
  }

  async getShift(id: string): Promise<Shift | undefined> {
    const snap = await forge.doc(`shifts/${id}`).get();
    return snap.exists ? (snap.data() as unknown as Shift) : undefined;
  }

  async saveShift(shift: Shift): Promise<void> {
    await forge.doc(`shifts/${shift.id}`).set({ ...shift });
  }

  async deleteShift(id: string): Promise<void> {
    await forge.doc(`shifts/${id}`).delete();
  }

  /* ─── Schedules ────────────────────────────────────────── */

  async getSchedules(): Promise<AssignmentSchedule[]> {
    const snap = await forge.collection('schedules').query().get();
    return snap.docs.map((doc) => doc.data() as unknown as AssignmentSchedule);
  }

  async getSchedule(id: string): Promise<AssignmentSchedule | undefined> {
    const snap = await forge.doc(`schedules/${id}`).get();
    return snap.exists ? (snap.data() as unknown as AssignmentSchedule) : undefined;
  }

  async getScheduleByShiftId(shiftId: string): Promise<AssignmentSchedule | undefined> {
    const snap = await forge
      .collection('schedules')
      .query(where('shiftId', '==', shiftId))
      .get();
    return snap.empty ? undefined : (snap.docs[0]?.data() as unknown as AssignmentSchedule);
  }

  async saveSchedule(schedule: AssignmentSchedule): Promise<void> {
    await forge.doc(`schedules/${schedule.id}`).set({ ...schedule });
  }

  async deleteSchedule(id: string): Promise<void> {
    await forge.doc(`schedules/${id}`).delete();
  }

  /* ─── Burden Scores ────────────────────────────────────── */

  async getBurdenScores(): Promise<BurdenScore[]> {
    const snap = await forge.collection('burdenScores').query().get();
    return snap.docs.map((doc) => doc.data() as unknown as BurdenScore);
  }

  async getBurdenScore(memberId: string): Promise<BurdenScore | undefined> {
    const snap = await forge.doc(`burdenScores/${memberId}`).get();
    return snap.exists ? (snap.data() as unknown as BurdenScore) : undefined;
  }

  async saveBurdenScore(score: BurdenScore): Promise<void> {
    await forge.doc(`burdenScores/${score.memberId}`).set({ ...score });
  }

  async saveBurdenScores(scores: BurdenScore[]): Promise<void> {
    const batch = forge.batch();
    for (const score of scores) {
      batch.set(`burdenScores/${score.memberId}`, { ...score });
    }
    await batch.commit();
  }

  async resetBurdenScores(): Promise<void> {
    const snap = await forge.collection('burdenScores').query().get();
    const batch = forge.batch();
    for (const doc of snap.docs) {
      batch.delete(`burdenScores/${doc.id}`);
    }
    await batch.commit();
  }

  /* ─── Workspace Export / Import ────────────────────────── */

  async exportAll(): Promise<WorkspaceExport> {
    const [organization, positions, members, shifts, schedules, burdenScores] =
      await Promise.all([
        this.getOrganization(),
        this.getPositions(),
        this.getMembers(),
        this.getShifts(),
        this.getSchedules(),
        this.getBurdenScores(),
      ]);

    return {
      schemaVersion: '1.0.0',
      exportedAt: new Date().toISOString(),
      organization: organization ?? {
        id: 'org',
        name: 'My Organization',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        defaultShiftDurationHours: 8,
        rotationIntervalMinutes: 120,
        enableSeniority: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      positions,
      members,
      shifts,
      schedules,
      burdenScores,
    };
  }

  async importAll(data: WorkspaceExport): Promise<void> {
    await this.resetAll();

    const batch = forge.batch();

    batch.set('organization/org', { ...data.organization });

    data.positions.forEach((p) => batch.set(`positions/${p.id}`, { ...p }));
    data.members.forEach((m) => batch.set(`members/${m.id}`, { ...m }));
    data.shifts.forEach((s) => batch.set(`shifts/${s.id}`, { ...s }));
    data.schedules.forEach((s) => batch.set(`schedules/${s.id}`, { ...s }));
    data.burdenScores.forEach((b) => batch.set(`burdenScores/${b.memberId}`, { ...b }));

    await batch.commit();
  }

  /* ─── Danger Zone ──────────────────────────────────────── */

  async resetAll(): Promise<void> {
    // Note: This relies on fetching all documents first, which might be slow for large datasets.
    // Real Firestore handles batch deletes differently, but this is a simple implementation.
    const collections = ['organization', 'positions', 'members', 'shifts', 'schedules', 'burdenScores'];
    
    for (const col of collections) {
      const snap = await forge.collection(col).query().get();
      if (snap.empty) continue;
      
      const batch = forge.batch();
      for (const doc of snap.docs) {
        batch.delete(`${col}/${doc.id}`);
      }
      await batch.commit();
    }
  }
}
